// Tour definitions for different user flows

export const TOURS = {
  // Customer Tours
  CUSTOMER_WELCOME: {
    id: 'customer_welcome',
    name: 'Welcome Tour',
    role: 'customer',
    steps: [
      {
        target: '[data-tour="dashboard-stats"]',
        title: 'Your Dashboard',
        content: 'This is your command center. Track all your jobs, active deliveries, and spending at a glance.',
        placement: 'bottom'
      },
      {
        target: '[data-tour="post-job-btn"]',
        title: 'Post Your First Job',
        content: 'Click here to post a new freight job. Providers in the area will be notified instantly!',
        placement: 'bottom',
        highlight: true
      },
      {
        target: '[data-tour="jobs-list"]',
        title: 'Your Jobs',
        content: 'All your posted jobs appear here. You can filter by status and track their progress.',
        placement: 'top'
      },
      {
        target: '[data-tour="notifications-btn"]',
        title: 'Notifications',
        content: 'Get notified when providers submit quotes on your jobs. Never miss an update!',
        placement: 'bottom'
      }
    ]
  },

  CUSTOMER_POST_JOB: {
    id: 'customer_post_job',
    name: 'How to Post a Job',
    role: 'customer',
    steps: [
      {
        target: '[data-tour="job-details-section"]',
        title: 'Job Details',
        content: 'Start by entering a clear title and description. Be specific about what you need transported.',
        placement: 'right'
      },
      {
        target: '[data-tour="load-type-field"]',
        title: 'Load Information',
        content: 'Select the type of freight and enter the weight. This helps providers prepare the right vehicle.',
        placement: 'right'
      },
      {
        target: '[data-tour="pickup-section"]',
        title: 'Pickup Location',
        content: 'Enter where the freight needs to be collected. Include the full address for accurate quotes.',
        placement: 'right'
      },
      {
        target: '[data-tour="delivery-section"]',
        title: 'Delivery Destination',
        content: 'Where should it be delivered? Add any special instructions or access requirements.',
        placement: 'right'
      },
      {
        target: '[data-tour="submit-job-btn"]',
        title: 'Post Your Job',
        content: 'All set! Click to post your job. Matching providers will be notified via app, email, and SMS.',
        placement: 'top',
        highlight: true
      }
    ]
  },

  CUSTOMER_MANAGE_QUOTES: {
    id: 'customer_manage_quotes',
    name: 'Managing Quotes',
    role: 'customer',
    steps: [
      {
        target: '[data-tour="quotes-section"]',
        title: 'View Quotes',
        content: 'Providers submit quotes here. Compare prices, delivery times, and provider ratings.',
        placement: 'left'
      },
      {
        target: '[data-tour="provider-info"]',
        title: 'Provider Details',
        content: 'Check the provider\'s rating, reviews, and verification status before accepting.',
        placement: 'left'
      },
      {
        target: '[data-tour="accept-quote-btn"]',
        title: 'Accept a Quote',
        content: 'Found the right provider? Click Accept to confirm. Payment is held securely until delivery.',
        placement: 'top',
        highlight: true
      }
    ]
  },

  // Provider Tours
  PROVIDER_WELCOME: {
    id: 'provider_welcome',
    name: 'Welcome Tour',
    role: 'provider',
    steps: [
      {
        target: '[data-tour="trial-status"]',
        title: 'Your Free Trial',
        content: 'You have 14 days to explore! Full access to jobs and unlimited quotes. No charges until trial ends.',
        placement: 'bottom'
      },
      {
        target: '[data-tour="provider-stats"]',
        title: 'Your Stats',
        content: 'Track your performance: quotes sent, jobs won, active deliveries, and total earnings.',
        placement: 'bottom'
      },
      {
        target: '[data-tour="available-jobs"]',
        title: 'Available Jobs',
        content: 'Jobs matching your location appear here. The closer to your postcode, the better the match!',
        placement: 'top'
      },
      {
        target: '[data-tour="submit-quote-btn"]',
        title: 'Submit Quotes',
        content: 'See a job you want? Submit a competitive quote. Include pickup/delivery estimates to win more jobs.',
        placement: 'left',
        highlight: true
      },
      {
        target: '[data-tour="setup-payouts"]',
        title: 'Setup Payouts',
        content: 'Connect your bank account to receive payments directly when jobs are completed.',
        placement: 'left'
      }
    ]
  },

  PROVIDER_SUBMIT_QUOTE: {
    id: 'provider_submit_quote',
    name: 'Submitting a Quote',
    role: 'provider',
    steps: [
      {
        target: '[data-tour="quote-price"]',
        title: 'Set Your Price',
        content: 'Enter your quote price in AUD. Remember: a 10% platform fee applies to protect your earnings.',
        placement: 'right'
      },
      {
        target: '[data-tour="quote-pickup-time"]',
        title: 'Pickup Estimate',
        content: 'When can you pick up? Be realistic - customers appreciate accurate timelines.',
        placement: 'right'
      },
      {
        target: '[data-tour="quote-delivery-time"]',
        title: 'Delivery Estimate',
        content: 'When will you deliver? Factor in distance and any potential delays.',
        placement: 'right'
      },
      {
        target: '[data-tour="quote-notes"]',
        title: 'Add Notes',
        content: 'Stand out! Mention your experience with similar loads or any special equipment you have.',
        placement: 'right'
      },
      {
        target: '[data-tour="submit-quote-confirm"]',
        title: 'Submit Quote',
        content: 'Ready? Submit your quote. The customer will be notified immediately!',
        placement: 'top',
        highlight: true
      }
    ]
  },

  PROVIDER_MANAGE_JOBS: {
    id: 'provider_manage_jobs',
    name: 'Managing Your Jobs',
    role: 'provider',
    steps: [
      {
        target: '[data-tour="my-quotes-tab"]',
        title: 'Your Quotes',
        content: 'Track all quotes you\'ve submitted. See which are pending, accepted, or declined.',
        placement: 'bottom'
      },
      {
        target: '[data-tour="my-jobs-tab"]',
        title: 'Your Active Jobs',
        content: 'Jobs you\'ve won appear here. Update status as you pick up and deliver.',
        placement: 'bottom'
      },
      {
        target: '[data-tour="job-status-update"]',
        title: 'Update Job Status',
        content: 'Mark as "Picked Up" when you collect, and "Delivered" when complete. Customer gets notified!',
        placement: 'left',
        highlight: true
      }
    ]
  }
};

export const getToursByRole = (role) => {
  return Object.values(TOURS).filter(tour => tour.role === role);
};

export const getTourById = (tourId) => {
  return Object.values(TOURS).find(tour => tour.id === tourId);
};
